import {
	axiosErrorHandler,
	get,
	post,
	put,
	deletes,
} from 'models/shared/helpers/axiosInstance';
import { PaginatedDocuments } from 'models/shared/types/pagination';
import { buildPathWithQueryParams } from 'models/shared/helpers/utils';
import { getToken, getUser } from 'localStorage/authStorage';
import {
	CreateSauronConversationDto,
	UpdateSauronConversationDto,
	QuerySauronConversationDto,
	SauronConversation,
	SauronConversationStats,
	SauronWebhookRequestDto,
} from '../interfaces/sauron-conversation.interface';

const baseSauronConversationPath = '/v1/sauron-conversations';

export const createSauronConversation = async (
	createDto: CreateSauronConversationDto,
): Promise<SauronConversation> => {
	try {
		const res = await post<SauronConversation>(
			baseSauronConversationPath,
			createDto,
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversations = async (
	query: QuerySauronConversationDto,
): Promise<PaginatedDocuments<SauronConversation>> => {
	try {
		const res = await get<PaginatedDocuments<SauronConversation>>(
			buildPathWithQueryParams(baseSauronConversationPath, query),
		);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversationStats =
	async (): Promise<SauronConversationStats> => {
		try {
			const url = `${baseSauronConversationPath}/stats`;
			const res = await get<SauronConversationStats>(url);
			return res.data;
		} catch (err) {
			throw axiosErrorHandler(err);
		}
	};

export const fetchSauronConversationsBySessionId = async (
	sessionId: string,
): Promise<SauronConversation[]> => {
	try {
		const url = `${baseSauronConversationPath}/session/${sessionId}`;
		const res = await get<SauronConversation[]>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversationsBySender = async (
	sender: string,
): Promise<SauronConversation[]> => {
	try {
		const url = `${baseSauronConversationPath}/sender/${sender}`;
		const res = await get<SauronConversation[]>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const fetchSauronConversation = async (
	id: string,
): Promise<SauronConversation> => {
	try {
		const url = `${baseSauronConversationPath}/${id}`;
		const res = await get<SauronConversation>(url);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const updateSauronConversation = async (
	id: string,
	updateDto: UpdateSauronConversationDto,
): Promise<SauronConversation> => {
	try {
		const url = `${baseSauronConversationPath}/${id}`;
		const res = await put<SauronConversation>(url, updateDto);
		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const deleteSauronConversation = async (id: string): Promise<void> => {
	try {
		const url = `${baseSauronConversationPath}/${id}`;
		await deletes(url);
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};

export const deleteSauronConversationsBySessionId = async (
	sessionId: string,
): Promise<void> => {
	try {
		const url = `${baseSauronConversationPath}/session/${sessionId}`;
		await deletes(url);
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
export const callSauronChat = async (
	webhookData: Omit<SauronWebhookRequestDto, 'domain' | 'user'>,
	type: 'Tile' | 'GPS' | 'Sauron',
): Promise<SauronConversation> => {
	try {
		// Validar que la URL del webhook esté configurada
		const n8nWebhookUrl =
			process.env[`REACT_APP_N8N_${type.toLocaleUpperCase()}_URL`] || '';

		if (!n8nWebhookUrl) {
			const msg = `REACT_APP_N8N_${type.toLocaleUpperCase()}_URL no está configurada en las variables de entorno`;
			throw new Error(msg);
		}

		// Obtener el domain desde las variables de entorno
		const domain =
			process.env.REACT_APP_API_BASE_URL ||
			'https://api-pandaboard.dev.ozon.mobi/';

		// Construir el body completo con el domain
		const requestBody: SauronWebhookRequestDto = {
			...webhookData,
			domain,
			user: getUser()?.userId || '',
		};

		// Llamar al webhook de n8n con los headers de autenticación
		const res = await post<SauronConversation>(n8nWebhookUrl, requestBody, {
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${getToken()}`,
			},
		});

		return res.data;
	} catch (err) {
		throw axiosErrorHandler(err);
	}
};
